import { Form, formProviderHOC } from '@sravni/cosago-react-library/lib/components';
import { useFormContext } from '@sravni/cosago-react-library/lib/hooks';
import type { FormComponentProps } from '@sravni/cosago-react-library/lib/types';
import { useIsMobile } from '@sravni/react-utils';
import cn from 'classnames';
import type { FC } from 'react';
import React, { useEffect } from 'react';

import { sendEventShowStep } from 'shared/lib/sendGAEvents';
import { useCommonFormBodyHooks } from 'shared/lib/useCommonFormBodyHooks';
import type { TCommonAdditionalFormProps } from 'shared/types';
import { FormStickyButton } from 'shared/ui';
import { AdaptivePopover } from 'shared/ui/AdaptivePopover';
import { FormWithControlledNavigation } from 'shared/ui/FormWithControlledNavigation';

import { useSendFieldErrorAnalyticsEvent } from 'entities/creditForm/lib/useSendFieldErrorAnalyticsEvent';
import { type TWorkplaceFormFields } from 'src/client/entities/creditForm';
import { useDevFillMockDataTool } from 'src/client/entities/creditForm/lib/useDevFillMockDataTool';

import { WorkplaceInfoHintsTexts, WorkplaceInfoTexts } from 'features/UpdateWorkplaceInfo';
import { useWorkLocationRouteLoader } from 'features/UpdateWorkplaceInfo/lib/useAddressLocationRouteLoader';
import { useClearUnnecessaryFields } from 'features/UpdateWorkplaceInfo/lib/useClearUnnecessaryFields';
import { useInitialJobPositionSuggestions } from 'features/UpdateWorkplaceInfo/lib/useInitialJobPositionSuggestions';
import { useIsShowWorkFields } from 'features/UpdateWorkplaceInfo/lib/useIsShowWorkFields';
import { ImportantDataAlert } from 'features/UpdateWorkplaceInfo/ui/ImportantDataAlert/ImportantDataAlert';
import { OrganizationSummary } from 'features/UpdateWorkplaceInfo/ui/OrganizationSummary';
import { FormFieldsNavigationSequence } from 'features/UpdateWorkplaceInfo/WorkplaceInfo.config';

import { useAccordionHeaderParams } from '../../lib/useAccordionHeaderParams';

import styles from './FormBody.module.scss';

type TWorkplaceInfoFormView = TCommonAdditionalFormProps & {
  shouldRestoreAdditionalData: boolean;
};

type Props = FormComponentProps<TWorkplaceFormFields, TWorkplaceInfoFormView>;

const WorkplaceInfoFormView: FC<Props> = ({
  isDialog,
  onSubmit,
  NAMES,
  submitButtonText,
  FieldConstructor,
  isLoading,
  additionalProps,
}) => {
  const { setHeader, isFormForceOpened, formId, isAnyFormStepOpenedInPopup, analyticsOverrides } =
    additionalProps || {};
  const isMobile = useIsMobile();
  const isStickyButton = !isDialog && isMobile && !isAnyFormStepOpenedInPopup;

  const { watch } = useFormContext<TWorkplaceFormFields>();

  const [title, subtitle] = useAccordionHeaderParams(watch(), {}, false);

  const { controls } = useCommonFormBodyHooks({
    currentControls: FormFieldsNavigationSequence,
    isFormForceOpened,
    headerData: {
      title,
      subtitle,
    },
    setHeader,
  });

  useEffect(() => {
    sendEventShowStep(4, 'Работа', analyticsOverrides);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useInitialJobPositionSuggestions();
  useWorkLocationRouteLoader();

  useSendFieldErrorAnalyticsEvent<TWorkplaceFormFields>(4, WorkplaceInfoTexts, analyticsOverrides);
  useDevFillMockDataTool<TWorkplaceFormFields>({
    type: 'workData',
    stepLabel: 'Работа',
  });

  const {
    isAnotherReason,
    isNotEmployed,
    isWorking,
    isWorkSummaryAvailable,
    isShowJobFields,
    isWorkOrganizationNameEmpty,
  } = useIsShowWorkFields();

  useClearUnnecessaryFields(watch());

  const isShowOrganizationFields = !isWorkSummaryAvailable && !isWorkOrganizationNameEmpty;

  return (
    <FormWithControlledNavigation
      navigationControls={controls}
      onSubmit={onSubmit}
      controlElement={FieldConstructor}
      isDialog={isDialog}
      isLoading={isLoading}
      submitButtonText={submitButtonText}
      formId={formId}
      withSubmitButtons={!isStickyButton}
    >
      <Form.Block>
        <Form.Row colWidths={[12]}>
          <ImportantDataAlert />
        </Form.Row>
        <Form.Row colWidths={[4, isWorking ? 8 : 4]}>
          <FieldConstructor type={NAMES.employmentType} />
          {isWorking && <FieldConstructor type={NAMES.workOrganizationName} />}
          {!isWorking && !isNotEmployed && <FieldConstructor type={NAMES.monthlySalary} />}
        </Form.Row>
        {isWorking && (
          <>
            <div className={cn({ [styles.hiddenRow]: !isWorkSummaryAvailable })}>
              <Form.Row colWidths={[12]}>
                <OrganizationSummary />
              </Form.Row>
            </div>
            <div className={cn({ [styles.hiddenRow]: !isShowOrganizationFields })}>
              <Form.Row>
                <FieldConstructor type={NAMES.employerType} />
                <FieldConstructor type={NAMES.areaOfEmployment} />
                <FieldConstructor type={NAMES.employerInn} />
              </Form.Row>
              <Form.Row colWidths={[12]}>
                <FieldConstructor type={NAMES.workAddress} />
              </Form.Row>
            </div>
            <Form.Row>
              <AdaptivePopover
                placement="top"
                content={WorkplaceInfoHintsTexts.lastWorkExperienceStartMonth}
              >
                <FieldConstructor type={NAMES.lastWorkExperienceStartMonth} />
              </AdaptivePopover>
              <FieldConstructor type={NAMES.workPhone} />
              {isShowJobFields && <FieldConstructor type={NAMES.jobTitle} />}
            </Form.Row>
            <Form.Row colWidths={[4]}>
              <AdaptivePopover
                placement="top"
                content={WorkplaceInfoHintsTexts.monthlySalary}
              >
                <FieldConstructor type={NAMES.monthlySalary} />
              </AdaptivePopover>
            </Form.Row>
          </>
        )}
        {isNotEmployed && (
          <Form.Row colWidths={[8]}>
            <FieldConstructor type={NAMES.unemploymentReason} />
          </Form.Row>
        )}
        {isNotEmployed && isAnotherReason && (
          <Form.Row colWidths={[12]}>
            <FieldConstructor type={NAMES.anotherUnemploymentReason} />
          </Form.Row>
        )}
      </Form.Block>

      <FormStickyButton
        onClick={onSubmit}
        enabled={isStickyButton}
        isLoading={isLoading}
      >
        {submitButtonText}
      </FormStickyButton>
    </FormWithControlledNavigation>
  );
};

export const WorkplaceInfoViewHoc = formProviderHOC<TWorkplaceFormFields, TWorkplaceInfoFormView>(
  WorkplaceInfoFormView,
);
